import React, { useState } from "react";
import api from "../../../api";
import utils from "../../../utils/utils";
import DialogTitle from "@material-ui/core/DialogTitle";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Button,
  Dialog,
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { UserCreate, Role, PersonalType } from "../../../enums/enums";

const CreateSchema = Yup.object().shape({
  name: Yup.string().required().matches("[a-zA-Z]"),
  email: Yup.string().email().required(),
  phone: Yup.number().required().positive().integer(),
  username: Yup.string().required().max(10),
  password: Yup.string().required().min(8).max(16),
  confirmPassword: Yup.string().required().min(8).max(16),
});

const CustomTextField = ({
  Name,
  Label,
  Value,
  Type,
  OnBlur,
  OnChange,
  InputProps,
  Error,
}) => (
  <Field
    as={TextField}
    name={Name}
    label={Label}
    value={Value}
    onChange={OnChange}
    onBlur={OnBlur}
    type={Type ?? "text"}
    variant="outlined"
    fullWidth
    size="small"
    inputProps={InputProps}
    error={Error ?? false}
  />
);

const CustomSelect = ({ Name, Label, Value, OnChange, Items }) => (
  <FormControl variant="outlined" size="small" fullWidth>
    <InputLabel>{Label}</InputLabel>
    <Select name={Name} value={Value} onChange={OnChange} label={Label}>
      {Items &&
        Items.map((item) => (
          <MenuItem value={item.value}>{item.text}</MenuItem>
        ))}
    </Select>
  </FormControl>
);

export default function UserCreateDialog({
  Open,
  Close,
  UserRole,
  SetIsLoading,
  DisplayMessage,
}) {
  const [isPersonal, setIsPersonal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [typeRole, setTypeRole] = useState();
  const [userSelected, setUserSelected] = useState();
  const [userTypeSelected, setUserTypeSelected] = useState();
  const [userCompanySelected, setUserCompanySelected] = useState();
  const [userPersonalSelected, setUserPersonalSelected] = useState();
  const [userPersonalData, setUserPersonalData] = useState();
  const [loading, setLoading] = useState(false);
  const [companiesList, setCompaniesList] = useState();
  const [personalList, setPersonalList] = useState();

  function getCompanies() {
    loadingOpen();
    api
      .getCompanies()
      .then((res) => {
        setCompaniesList(utils.setDataInList(res.data));
      })
      .catch(() => {
        DisplayMessage("Ocurrió un error");
        loadingOpen();
      })
      .then(() => LoadingClose());
  }

  function selectUserSelected(type) {
    loadingOpen();
    api
      .getCompanies()
      .then((res) => {
        setCompaniesList(utils.setDataInList(res.data));
        setUserSelected(type);
        if (type === UserCreate.Personal) setIsPersonal(true);
      })
      .catch(() => DisplayMessage("Ocurrió un error"))
      .then(() => LoadingClose());
  }

  const loadingOpen = () => {
    SetIsLoading(true);
    setLoading(true);
  };

  const LoadingClose = () => {
    SetIsLoading(false);
    setLoading(false);
  };

  function searchPersonal(companyId, typeRole) {
    loadingOpen();
    if (userPersonalData) {
      setUserPersonalData(undefined);
      setUserPersonalSelected(undefined);
    }
    if (typeRole === Role.AdminComercial) {
      api
        .getPersonalSelectList()
        .then((res) => setPersonalList(utils.setDataInList(res.data)))
        .catch(() => DisplayMessage("Ocurrió un error"))
        .then(() => LoadingClose());
    } else {
      let personalType;

      if (typeRole === Role.InternalSeller) {
        personalType = PersonalType.PersonalSOS;
      } else if (typeRole === Role.ExternalSeller) {
        personalType = PersonalType.PersonalExternal;
      } else {
        personalType = PersonalType.PersonalExternal;
      }

      api
        .getPersonalByCompany(companyId, personalType)
        .then((res) => setPersonalList(utils.setDataInList(res.data)))
        .catch(() => DisplayMessage("Ocurrió un error"))
        .then(() => LoadingClose());
    }
  }

  function setPersonalSelected(id) {
    loadingOpen();
    setUserPersonalSelected(id);
    LoadingClose();
  }

  const onClose = () => {
    Close();
    setUserSelected(undefined);
  };

  function onSelectUserType(type) {
    setUserCompanySelected(undefined);
    switch (type) {
      case Role.AdminComercial:
        setIsAdmin(true);
        setTypeRole(Role.AdminComercial);
        searchPersonal(null, Role.AdminComercial);
        break;
      case Role.InternalSeller:
        setIsAdmin(false);
        getCompanies();
        setTypeRole(Role.InternalSeller);
        break;
      case Role.ExternalSeller:
        getCompanies();
        setIsAdmin(false);
        setTypeRole(Role.ExternalSeller);
        break;
      case Role.AdminIT:
        setIsAdmin(true);
        setTypeRole(Role.AdminIT);
        break;
      case Role.UserIntegration:
        getCompanies();
        setIsAdmin(false);
        setTypeRole(Role.ExternalSeller);
        break;
    }

    setUserTypeSelected(type);
  }

  function getRoles() {
    let response = [];

    if (UserRole === Role.AdminComercial) {
      response.push({ value: Role.ExternalSeller, text: "Vendedor Externo" });
      response.push({ value: Role.InternalSeller, text: "Vendedor Interno" });
      response.push({
        value: Role.UserIntegration,
        text: "Usuario Integración",
      });
    } else {
      response.push({ value: Role.AdminComercial, text: "Admin comercial" });
      response.push({ value: Role.ExternalSeller, text: "Vendedor Externo" });
      response.push({ value: Role.InternalSeller, text: "Vendedor Interno" });
      response.push({
        value: Role.UserIntegration,
        text: "Usuario Integración",
      });
    }

    return response;
  }

  function onSubmit(e) {
    if (e.password !== e.confirmPassword) {
      DisplayMessage("Las contraseñas deben coincidir.");
      return;
    }

    loadingOpen();
    let data = {
      tipo: userTypeSelected,
      personaId: userPersonalSelected,
      companiaId: userCompanySelected,
    };

    api
      .postUser(data)
      .then((res) => {
        let data = res.data;
        if (data.result) {
          DisplayMessage("Creado correctamente!", "success");
          onClose();
        } else {
          DisplayMessage(data.message);
        }
      })
      .catch((err) => DisplayMessage("Ocurrió un error"))
      .then(() => LoadingClose());
  }

  return (
    <Dialog open={Open} onClose={onClose} fullWidth>
      <DialogTitle>Nuevo usuario</DialogTitle>
      <DialogContent className="c-mb-10">
        <Formik
          initialValues={{
            name: "",
            email: "",
            phone: "",
            username: "",
            password: "",
            confirmPassword: "",
          }}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(false);
            onSubmit(values);
          }}
        >
          {({
            values,
            errors,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <CustomSelect
                    Name="userType"
                    Label="Tipo de Usuario"
                    Value={userTypeSelected}
                    OnChange={(e) => onSelectUserType(e.target.value)}
                    Items={getRoles()}
                  />
                </Grid>
              </Grid>
              {userTypeSelected && !loading && (
                <Grid className="c-mt-5" container spacing={1}>
                  {!isAdmin && (
                    <>
                      <Grid item xs={12}>
                        <CustomSelect
                          Name="userCompany"
                          Label="Compañia"
                          Value={userCompanySelected}
                          OnChange={(e) => {
                            setUserCompanySelected(e.target.value);
                            searchPersonal(e.target.value, typeRole);
                          }}
                          Items={companiesList}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <Autocomplete
                      disableClearable
                      options={personalList}
                      getOptionLabel={(option) => option.text}
                      onChange={(e, c) => {
                        if (c) setPersonalSelected(c.value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Usuario"
                          name="userPersonal"
                          value={userPersonalSelected}
                          variant="outlined"
                          size="small"
                        />
                      )}
                      disabled={
                        (!userCompanySelected && !isAdmin) ||
                        (isAdmin && !userTypeSelected)
                      }
                    />
                  </Grid>
                  <Grid className="c-text-right" item xs={12}>
                    <Button className="c-btn-cancel c-mr-5" onClick={onClose}>
                      Cancelar
                    </Button>
                    <Button className="c-btn-success" type="submit">
                      Aceptar
                    </Button>
                  </Grid>
                </Grid>
              )}
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
